(ns yardwerkz.web.ui.layout
  "App shell: top navigation bar, content area, responsive container."
  (:require
   [uix.core :refer [defui $ use-state]]
   [io.factorhouse.rfx.core :as rfx]
   [yardwerkz.web.store :as store]
   [yardwerkz.web.router :as router]
   [yardwerkz.web.ui.icons :as icons]
   [yardwerkz.web.ui.components :as ui]))

;; =============================================================================
;; Navigation Items
;; =============================================================================

(def nav-items
  [{:id :book      :label "Book"     :icon icons/PlusCircle :route :book}
   {:id :jobs      :label "Jobs"     :icon icons/Briefcase  :route :jobs}
   {:id :messages  :label "Messages" :icon icons/MessageSquare :route :messages}
   {:id :profile   :label "Profile"  :icon icons/User       :route :profile}])

(defn- active-tab
  "Determine which nav tab is active based on current route."
  [route-match]
  (let [route-name (some-> route-match :data :name)]
    (cond
      (#{:home :book} route-name) :book
      (#{:jobs :job} route-name) :jobs
      (#{:messages :conversation} route-name) :messages
      (#{:profile :saved-addresses :edit-profile
         :payment-methods :payment-history :notification-prefs} route-name) :profile
      :else :book)))

;; =============================================================================
;; Top Navigation Bar (desktop)
;; =============================================================================

(defui nav-bar []
  (let [route-match (rfx/use-sub [::store/current-route])
        current-tab (active-tab route-match)
        unread-count (rfx/use-sub [::store/unread-count])]
    ;; Desktop top nav
    ($ :nav {:class "bg-white border-b border-gray-200 sticky top-0 z-40"}
       ($ :div {:class "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"}
          ($ :div {:class "flex items-center justify-between h-16"}
             ;; Logo
             ($ :a {:href (router/href :home)
                    :class "flex items-center space-x-2"}
                ($ :span {:class "text-xl font-bold text-green-600"} "Yardwerkz"))

             ;; Nav links (hidden on mobile)
             ($ :div {:class "hidden md:flex items-center space-x-1"}
                (for [{:keys [id label icon route]} nav-items]
                  ($ :a {:key id
                         :href (router/href route)
                         :class (str "flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors "
                                     (if (= current-tab id)
                                       "bg-green-50 text-green-700"
                                       "text-gray-600 hover:bg-gray-50 hover:text-gray-900"))}
                     ($ icon {:size 18})
                     ($ :span label)
                     (when (and (= id :messages) (pos? unread-count))
                       ($ :span {:class "ml-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center"}
                          unread-count)))))

             ;; Notification bell
             ($ :button {:class "relative p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
                         :on-click #(router/navigate! :profile)}
                ($ icons/Bell {:size 20})
                (when (pos? unread-count)
                  ($ :span {:class "absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full"}))))))))

;; =============================================================================
;; Bottom Tab Bar (mobile)
;; =============================================================================

(defui bottom-tab-bar []
  (let [route-match (rfx/use-sub [::store/current-route])
        current-tab (active-tab route-match)
        unread-count (rfx/use-sub [::store/unread-count])]
    ($ :nav {:class "md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 safe-area-inset-bottom"}
       ($ :div {:class "flex items-center justify-around h-16"}
          (for [{:keys [id label icon route]} nav-items]
            ($ :a {:key id
                   :href (router/href route)
                   :class (str "flex flex-col items-center justify-center flex-1 py-1 "
                               (if (= current-tab id)
                                 "text-green-600"
                                 "text-gray-400"))}
               ($ :div {:class "relative"}
                  ($ icon {:size 22})
                  (when (and (= id :messages) (pos? unread-count))
                    ($ :span {:class "absolute -top-1 -right-2 bg-red-500 text-white text-xs rounded-full px-1 min-w-[16px] text-center"}
                       unread-count)))
               ($ :span {:class "text-xs mt-0.5"} label)))))))

;; =============================================================================
;; App Shell
;; =============================================================================

(defui app-shell
  "Main application layout with navigation."
  [{:keys [children]}]
  ($ :div {:class "min-h-screen bg-gray-50"}
     ($ nav-bar)
     ;; Main content area with bottom padding for mobile tab bar
     ($ :main {:class "pb-20 md:pb-0"}
        children)
     ($ bottom-tab-bar)
     ($ ui/toast-container)))

;; =============================================================================
;; Page Container
;; =============================================================================

(defui page-container
  "Centered, width-constrained container for page content."
  [{:keys [children class full-width]}]
  ($ :div {:class (str (if full-width
                         "w-full"
                         "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8")
                       " py-6 " class)}
     children))
